import { KalturaAPIException } from '@kaltura-ott/ott-client-types/api';
import {
  KalturaAssetType,
  KalturaRecording,
  KalturaRecordingStatus,
  KalturaRecordingType,
  KalturaSeriesRecording,
} from '@kaltura-ott/ott-client-types/api/types';
import {
  addRecordingV2,
  addSeriesRecordingV2,
  cancelRecording,
  cancelSeriesRecording,
  deleteRecording,
  deleteRecordsList,
  getBookmarks,
  getHouseholdQuota,
  getRecordingById,
  getSingleAndSeriesRecordListV2,
  getSingleRecordsAssetsV2,
  HouseholdQuotaErrorCodeEnum,
  KuxRecord,
  KuxRecordingsListArgs,
  KuxRecordSpace,
  normalizingRecord,
  QuotaStatusEnum,
  RecordsErrorCodeEnum,
} from '@kaltura-ott/tvpil';
import { action, observable } from 'mobx';
import { lastValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { container, singleton } from 'tsyringe';

import { errorHandling } from '../../utils/error-handling';
import { AppParametersStore } from '../app/app-params-store';
import type { AppStore } from '../app/app-store';
import { Record, SeriesRecord } from '../entities';
import { ErrorPriority } from '../error';

import { DeleteRecordsTypesEnum, RecordArgs } from './record-repository-helper';

const SIX_MOTHS_IN_SECONDS = 15778476;
const SMART_TV_RECORDING_CHUNK_SIZE = 25;
const RECORDING_CHUNK_SIZE = 100;

@singleton()
export class RecordRepository {
  static moduleName = 'RecordRepository';

  @observable records: Map<string, Record> = new Map();
  @observable seriesRecords: Map<string, SeriesRecord> = new Map();

  @observable deleteRecordsOptions: DeleteRecordsTypesEnum[] = [];

  @observable space: KuxRecordSpace = {
    totalQuota: 0,
    availableQuota: 0,
    status: QuotaStatusEnum.NpvrServiceNotAllowed,
  };

  @observable currentRecordRailId?: number;

  @observable recordRailsMap: { [key: number]: { records?: number[]; lastIndex?: number } } = {};

  private get appStore() {
    return container.resolve<AppStore>('appStore');
  }

  private get recordingPageSize() {
    const appParametersStore = container.resolve(AppParametersStore);
    return appParametersStore.isSmartTv ? SMART_TV_RECORDING_CHUNK_SIZE : RECORDING_CHUNK_SIZE;
  }

  @errorHandling()
  getRecord(type: KalturaRecordingType, assetId?: number, seriesId?: string) {
    switch (type) {
      case KalturaRecordingType.single:
        return this.getSingleRecord(assetId);
      case KalturaRecordingType.series:
        return this.getSeriesRecord(seriesId);
      default:
        return;
    }
  }

  getSingleRecord(assetId?: number) {
    if (assetId) {
      return this.records.get(assetId.toString());
    }
    return undefined;
  }

  getSeriesRecord(seriesId?: string) {
    if (seriesId) {
      return this.seriesRecords.get(seriesId);
    }
  }

  addSingleRecordToRepository(assetId: number, record: KuxRecord) {
    this.records.set(assetId.toString(), new Record(record));
  }

  saveRailItems(array: number[], categoryId: number) {
    this.recordRailsMap[categoryId] = { ...this.recordRailsMap[categoryId], records: array };
  }

  saveCurrentAssetId(assetId?: number) {
    if (this.currentRecordRailId && assetId) {
      const index = this.recordRailsMap[this.currentRecordRailId].records?.findIndex((id) => id === assetId);
      const lastIndex = index !== -1 ? index : 0;
      this.recordRailsMap[this.currentRecordRailId] = {
        ...this.recordRailsMap[this.currentRecordRailId],
        lastIndex,
      };
    }
    this.currentRecordRailId = undefined;
  }

  saveCurrentRecordRailId(id: number) {
    this.currentRecordRailId = id;
  }

  getLastIndexByRailId(id: number) {
    return this.recordRailsMap[id]?.lastIndex;
  }

  @action
  async getAvailableSpace(): Promise<void> {
    try {
      const { totalQuota, availableQuota } = await lastValueFrom(getHouseholdQuota());
      this.space = {
        totalQuota,
        availableQuota,
        status: QuotaStatusEnum.NpvrLimitedQuota,
      };

      if (availableQuota > SIX_MOTHS_IN_SECONDS) {
        this.space.status = QuotaStatusEnum.NpvrUnlimitedQuota;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (
        e.code === HouseholdQuotaErrorCodeEnum.ServiceNotAllowed ||
        e.code === HouseholdQuotaErrorCodeEnum.AccountCdvrNotEnabled
      ) {
        console.warn(`HouseholdQuota not available for current user. Error code:${e.code}`);
        this.space = {
          totalQuota: 0,
          availableQuota: 0,
          status: QuotaStatusEnum.NpvrServiceNotAllowed,
        };
      } else {
        throw e;
      }
    }
  }
  @action.bound
  @errorHandling()
  public definingDeleteOptions = async (): Promise<void> => {
    let deleteRecordsOptions: DeleteRecordsTypesEnum[] = [];
    const records = Array.from(this.records.values()).filter(
      (record) => record.data.status === KalturaRecordingStatus.recorded,
    );
    const viewedRecords = await this.getViewedRecords(records);
    const olderThenOneWeekRecords = this.getOlderThenOneWeekRecords(records);
    if (records.length) {
      deleteRecordsOptions = deleteRecordsOptions.concat(DeleteRecordsTypesEnum.deleteAll);
    }

    if (viewedRecords.length) {
      deleteRecordsOptions = deleteRecordsOptions.concat(DeleteRecordsTypesEnum.deleteAllViewed);
    }

    if (olderThenOneWeekRecords.length) {
      deleteRecordsOptions = deleteRecordsOptions.concat(DeleteRecordsTypesEnum.deleteAllOlderTheOneWeek);
    }

    this.deleteRecordsOptions = deleteRecordsOptions;
  };

  @action
  @errorHandling({ type: ErrorPriority.low })
  async getAllRecordList() {
    const recordStatuses = [
      KalturaRecordingStatus.recorded,
      KalturaRecordingStatus.scheduled,
      KalturaRecordingStatus.recording,
    ];
    const { singles, series } = await lastValueFrom(
      getSingleAndSeriesRecordListV2(
        {
          statusIn: recordStatuses.join(', '),
        },
        this.recordingPageSize,
      ),
    );

    this.records = new Map();
    singles?.forEach((record) => {
      if (record.assetId) this.records.set(record.assetId.toString(), new Record(record));
    });

    this.seriesRecords = new Map();
    series?.forEach((seriesRecord) => {
      this.seriesRecords.set(seriesRecord.seriesId, new SeriesRecord(seriesRecord));
    });
  }

  async getSingleRecordsAssets(args: KuxRecordingsListArgs & { kSql?: string; groupBy?: string }) {
    return lastValueFrom(getSingleRecordsAssetsV2(args));
  }

  private async addSeriesRecord(epgId: number, seriesId: string) {
    const seriesResponse = await lastValueFrom(
      addSeriesRecordingV2(
        {
          type: KalturaRecordingType.series,
          epgId,
          seriesId,
        },
        this.recordingPageSize,
      ),
    );

    this.seriesRecords.set(seriesResponse.series.seriesId, new SeriesRecord(seriesResponse.series));

    seriesResponse.singles.forEach((record) => {
      if (!record || !record.assetId) {
        return;
      }
      const normalizedRecord = normalizingRecord(record);
      this.records.set(record.assetId.toString(), new Record(normalizedRecord));
    });
  }

  private async addSingleRecord(assetId: number) {
    const record = await lastValueFrom(addRecordingV2({ assetId }));
    if (!record || !record.assetId) {
      return;
    }

    const normalizedRecord = normalizingRecord(record);
    this.records.set(record.assetId.toString(), new Record(normalizedRecord));
  }

  @action
  @errorHandling()
  async addRecord({ assetId, seriesId }: RecordArgs) {
    try {
      if (!seriesId) {
        await this.addSingleRecord(assetId);
      } else {
        await this.addSeriesRecord(assetId, seriesId);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.code === RecordsErrorCodeEnum.ExceededQuota) {
        return {
          error: e.code,
          message: e.message,
        };
      } else {
        throw e;
      }
    }
    this.appStore.reInitRecordings();
  }

  private async removeSingleRecord(assetId: number) {
    const record = this.getSingleRecord(assetId);
    if (!record) {
      return;
    }

    // recheck status of record
    const newSingleRecord = await lastValueFrom(getRecordingById(record.data.id));

    const {
      data: { id, status },
    } = new Record(newSingleRecord);

    switch (status) {
      // delete for recorded status
      case KalturaRecordingStatus.recorded:
        await lastValueFrom(deleteRecording({ id }));
        break;
      // cancel for recording and scheduled
      case KalturaRecordingStatus.scheduled:
      case KalturaRecordingStatus.recording:
        await lastValueFrom(cancelRecording({ id }));
        break;
      default:
        console.log(`Record (id:${id}) has been already ${status}`); // failed, deleted, canceled
    }
    this.records.delete(assetId.toString());
  }

  private async removeSeriesRecord(seriesId: string) {
    const record = this.getSeriesRecord(seriesId);
    if (!record) {
      return;
    }

    await lastValueFrom(cancelSeriesRecording({ id: record.data.id }));
    this.seriesRecords.delete(seriesId);
  }

  @action
  @errorHandling()
  async removeRecord({ assetId, seriesId }: { assetId: number; seriesId?: string }, type: KalturaRecordingType) {
    switch (type) {
      case KalturaRecordingType.series:
        if (seriesId) {
          await this.removeSeriesRecord(seriesId);
        }
        break;
      case KalturaRecordingType.single:
        await this.removeSingleRecord(assetId);
        break;
    }

    this.appStore.reInitRecordings();
  }

  @action
  @errorHandling()
  async deleteRecords(type: DeleteRecordsTypesEnum) {
    const allRecords = (
      await lastValueFrom(
        getSingleAndSeriesRecordListV2({ statusIn: KalturaRecordingStatus.recorded }, this.recordingPageSize),
      )
    ).singles.map((kuxRecord) => new Record(kuxRecord));
    // exclude general series records from /seriesrecording/action
    let records = allRecords;
    if (type === DeleteRecordsTypesEnum.deleteAllOlderTheOneWeek) {
      records = this.getOlderThenOneWeekRecords(records);
    }

    if (type === DeleteRecordsTypesEnum.deleteAllViewed) {
      records = await this.getViewedRecords(records);
    }

    // @ts-expect-error TODO Fix
    const result: (KalturaRecording | KalturaSeriesRecording)[] = await lastValueFrom(
      deleteRecordsList(records.map((record) => record.data)),
    );

    const errors = result?.filter((record) => record instanceof KalturaAPIException);

    if (errors && errors.length !== 0) {
      throw new Error(JSON.stringify(errors));
    }
    result?.forEach((record) => {
      if ('assetId' in record) {
        this.records.delete(record.assetId.toString());
      } else {
        this.seriesRecords.delete(record.seriesId);
      }
    });

    this.appStore.reInitRecordings();

    await this.getAvailableSpace();
    await this.definingDeleteOptions();
  }

  @errorHandling()
  getOlderThenOneWeekRecords = (records: Record[]): Record[] => {
    const days = 7;
    const week = days * 24 * 60 * 60 * 1000;
    const filteredRecords =
      records.filter((record) => {
        const currentDiff = record?.data?.createDate
          ? new Date().getTime() - Number(record?.data?.createDate * 1000)
          : 0;
        return currentDiff > week;
      }) || [];

    return filteredRecords;
  };

  @errorHandling()
  getViewedRecords = async (records: Record[]): Promise<Record[]> => {
    const finishedWatchingBookmarks = await lastValueFrom(
      getBookmarks(records.map((record) => record.data.id).join(','), KalturaAssetType.recording).pipe(
        map(
          (bookmarks) =>
            bookmarks.objects?.filter((bookmark) => bookmark.finishedWatching).map((bookmark) => Number(bookmark.id)) ||
            [],
        ),
        catchError((error) => of(error)),
      ),
    );

    return records.filter((record) => finishedWatchingBookmarks.includes(record?.data.id)) || [];
  };
}
